import { useEffect, useRef } from "react";
import { ADMIN_IDLE_TIMEOUT_SEC } from "@/lib/session-policy";

/**
 * Admin idle-timeout watcher (Task #3061).
 *
 * Mounted inside the admin shell. Tracks the last user-interaction
 * timestamp via the standard activity events and, after
 * `ADMIN_IDLE_TIMEOUT_SEC` of silence, invokes `onTimeout` (which the
 * caller wires to `signOut() + redirect to /login`).
 *
 * Uses a single deadline-driven `setTimeout` rather than a ticking
 * interval — we update the deadline on each interaction and only fire
 * when no events have arrived for the full window. That keeps the
 * watcher quiet (no per-second wakeups) and avoids the off-by-one
 * drift that comes with `setInterval`.
 *
 * Passive listeners + `{ capture: true }` so we still observe events
 * even when an inner element calls `stopPropagation`. We deliberately
 * do NOT call `preventDefault` anywhere — this hook only observes.
 */
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "wheel", "visibilitychange"] as const;

export function useAdminIdleTimeout(onTimeout: () => void, enabled: boolean = true): void {
  // Ref the callback so consumers can pass a fresh closure each render
  // without resetting the timer.
  const cbRef = useRef(onTimeout);
  useEffect(() => { cbRef.current = onTimeout; }, [onTimeout]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    const timeoutMs = ADMIN_IDLE_TIMEOUT_SEC * 1000;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let fired = false;

    const reset = () => {
      if (fired) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        fired = true;
        try { cbRef.current(); } catch { /* noop */ }
      }, timeoutMs);
    };

    const onActivity = () => {
      // visibilitychange counts as activity only when the tab comes
      // back into focus — a hidden tab is exactly the case we want to
      // catch with the idle timeout.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      reset();
    };

    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true, capture: true });
    }
    reset();

    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, onActivity, { capture: true } as EventListenerOptions);
      }
      if (timer) clearTimeout(timer);
    };
  }, [enabled]);
}
